import { CANONICAL_STATES } from "@/format";
import type { Pipeline, PipelineStatus } from "@career-ops-amplify/shared";

export type PipelineStageName = (typeof CANONICAL_STATES)[number];

export const STAGE_PROMPT_CATEGORY: Record<PipelineStageName, string> = {
  Evaluated: "job-evaluation",
  Applied: "job-application",
  Responded: "job-followup",
  Interview: "job-interview",
  Offer: "job-offer",
  Rejected: "job-reflection",
  Discarded: "job-reflection",
  SKIP: "job-monitor",
};

export const STAGE_DESCRIPTIONS: Record<PipelineStageName, string> = {
  Evaluated: "Score the job against your profile and decide whether to pursue it.",
  Applied: "Track your submission and generate tailored application materials.",
  Responded: "Manage recruiter replies and follow-up outreach.",
  Interview: "Prepare for and debrief each interview round.",
  Offer: "Review compensation and plan your negotiation.",
  Rejected: "Capture learnings and spot patterns across rejections.",
  Discarded: "Record why you passed and move on.",
  SKIP: "Monitor without applying — keep on your radar.",
};

export const STAGE_TRANSITIONS: Record<PipelineStageName, PipelineStageName[]> = {
  Evaluated: ["Applied", "SKIP", "Discarded"],
  Applied: ["Responded", "Rejected", "Discarded"],
  Responded: ["Interview", "Rejected", "Discarded"],
  Interview: ["Offer", "Rejected", "Discarded"],
  Offer: ["Discarded"],
  Rejected: ["Discarded"],
  Discarded: [],
  SKIP: ["Evaluated", "Discarded"],
};

export const TERMINAL_STAGES = new Set<PipelineStageName>(["Rejected", "Discarded", "SKIP"]);

export const EVAL_KIND_ORDER: Record<string, number> = {
  evaluate: 0,
  oferta: 1,
};

export function stagePromptCategory(stageName: string): string | undefined {
  return STAGE_PROMPT_CATEGORY[stageName as PipelineStageName];
}

export function isValidStageName(name: string): name is PipelineStageName {
  return (CANONICAL_STATES as readonly string[]).includes(name);
}

export function defaultStageTab(stage?: string): PipelineStageName {
  if (stage && isValidStageName(stage)) return stage;
  return "Evaluated";
}

export function isPipelineArchived(pipeline: Pipeline): boolean {
  return pipeline.status === "archived";
}

/** Status to apply when restoring a pipeline from the archived list. */
export function restorePipelineStatus(pipeline: Pipeline): PipelineStatus {
  const stage = pipeline.currentStage;
  if (stage && TERMINAL_STAGES.has(stage as PipelineStageName)) {
    return "completed";
  }
  return "active";
}

export function canArchivePipeline(pipeline: Pipeline): boolean {
  if (isPipelineArchived(pipeline)) return false;
  const stage = pipeline.currentStage;
  return !!stage && TERMINAL_STAGES.has(stage as PipelineStageName);
}
